import { ImportCustomers } from "./modules/users/ImportCustomers";
import { Routes, Route, Navigate } from "react-router-dom";
import { RequireAuth } from "./auth/RequireAuth";
import { Callback } from "./auth/Callback";
import { Landing } from "./routes/Landing";
import { SignIn } from "./routes/SignIn";
import { EmailCodeHelp } from "./routes/EmailCodeHelp";
import { OperatorRegister } from "./modules/onboarding/OperatorRegister";
import { Register } from "./routes/Register";
import { Pricing } from "./routes/Pricing";
import { Contact } from "./routes/Contact";
import { ConfirmEmail } from "./routes/ConfirmEmail";
import { NamespaceDoc } from "./routes/NamespaceDoc";
import { LegalIndex } from "./routes/LegalIndex";
import { LegalDoc } from "./routes/LegalDoc";
import { SmsLink } from "./routes/SmsLink";
import { SMS_LINK_SLUGS } from "./domain/smsLinks";
import { Shell } from "./components/Shell";
import { Welcome } from "./modules/welcome/Welcome";
import { ProvisioningScreen, SettingsScreen } from "./modules/provisioning/Provisioning";
import { ProvidersScreen } from "./modules/providers/Providers";
import { StaffScreen } from "./modules/staff/Staff";
import { AddProviderScreen } from "./modules/providers/AddProvider";
import { PinOrdersScreen } from "./modules/providers/PinOrders";
import { PinPlansScreen } from "./modules/providers/PinPlans";
import { SubscriberProfilesScreen } from "./modules/users/SubscriberProfiles";
import { RechargeScreen } from "./modules/users/RechargeScreen";
import { CustomerLookupScreen } from "./modules/users/CustomerLookup";
import { SecurityScreen } from "./modules/account/Security";
import { ReportPage } from "./modules/reports/ReportPage";
import { REPORT_SPECS } from "./modules/reports/catalog";
import { ReportCategoryPage } from "./modules/reports/ReportCategoryPage";
import { REPORT_CATEGORIES } from "./components/nav";
import { CreatePlan } from "./modules/plans/CreatePlan";
import { PlansOnSale } from "./modules/plans/PlansOnSale";
import { PlansPromotions } from "./modules/plans/PlansPromotions";
import { PromotionReport } from "./modules/plans/PromotionReport";
import { ProposePriceList } from "./modules/plans/ProposePriceList";
import { CreatePlanFromPrompt } from "./modules/plans/CreatePlanFromPrompt";
import { PriceListScreen } from "./modules/plans/PriceList";
import { PriceListDocumentsScreen } from "./modules/plans/PriceListDocuments";
import { ComponentsScreen } from "./modules/plans/Components";
import { CreateUser } from "./modules/users/CreateUser";
import { OperatorProfileScreen } from "./modules/operator/OperatorProfile";
import { PortalAppearanceScreen } from "./modules/appearance/Appearance";
import { LegalDocumentsScreen } from "./modules/legal/LegalDocuments";
import { RosterScreen } from "./modules/roster/RosterScreen";
import { OrganisationsScreen } from "./modules/organisations/OrganisationsScreen";
import { OrganisationPage } from "./modules/organisations/OrganisationPage";
import { SponsorClaim } from "./routes/SponsorClaim";
import { AcceptInvite } from "./routes/AcceptInvite";
import { RecordPaymentScreen } from "./modules/payments/RecordPayment";
import { PaymentGatewayScreen } from "./modules/payments/PaymentGateway";
import { SmsGatewayScreen } from "./modules/sms/SmsGateway";
import { SmsFundScreen } from "./modules/sms/SmsFund";
import { SmsFundsPlatformScreen } from "./modules/sms/SmsFundsPlatform";
import { UpiNoticesScreen } from "./modules/payments/UpiNotices";
import { ConnectionRequestsScreen } from "./modules/payments/ConnectionRequests";
import { AnkhInvoicesScreen } from "./modules/billing/AnkhInvoices";
import { IssuedBillingScreen } from "./modules/billing/IssuedBilling";
import { UpstreamScreen } from "./modules/billing/Upstream";
import { InvoiceNameScreen } from "./modules/billing/InvoiceName";
import { AnkhCustomersScreen } from "./modules/billing/AnkhCustomers";
import { PlatformScreen } from "./modules/platform/Platform";
import { PlatformPlansScreen } from "./modules/platform/PlatformPlans";
import { flagEnabled } from "./config";
import { PinnedMode } from "./theme/ColorModeProvider";

export default function App() {
  return (
    <Routes>
      {/* ⛔ Must NOT be <Navigate>. See auth/Callback.tsx — it caused an
          infinite "Signing in…" loop by stripping ?code= before the
          AuthProvider could read it. */}
      <Route path="/callback" element={<Callback />} />
      {/* ⛔ PUBLIC. These two sit OUTSIDE the RequireAuth tree deliberately —
          putting them inside would make RequireAuth redirect to /welcome,
          which would render RequireAuth again. They must never read identity
          or call the gateway; see the note in Landing.tsx. */}
      <Route path="/welcome" element={<PinnedMode mode="dark"><Landing /></PinnedMode>} />
      {/* The embedded sign-in form. ZITADEL 302s every authorize request for
          this app to /auth/login?authRequest=… (the app's loginV2 baseUri is
          https://ankhos.io/auth). PUBLIC, outside RequireAuth, same rules as
          /welcome. The wildcard also takes /auth/logout, which the page
          forwards. See routes/SignIn.tsx and api/login.ts. */}
      {/* Where the button in a sign-in code email lands. radaccess points it
          here instead of ZITADEL's own hosted login, which knows nothing about a
          sign-in this console drives. It explains; it cannot sign anyone in. */}
      <Route path="/auth/code" element={<PinnedMode mode="dark"><EmailCodeHelp /></PinnedMode>} />
      <Route path="/auth/*" element={<PinnedMode mode="dark"><SignIn /></PinnedMode>} />
      {/* Sign-up first, compliance details after sign-in (owner plan 2026-09-13).
          Behind onboarding.signup: on, /register is the mobile-first sign-up
          (routes/Register.tsx, POST /access/operator/signup); off, it stays the
          25-field application until the owner switches live over. */}
      <Route path="/register" element={flagEnabled("onboarding.signup") ? <Register /> : <OperatorRegister />} />
      {/* /join is the address the io.ankhos invitation SMS gives (DLT text
          operator_join_invite, "Visit https://ankhos.io/join") and assisted
          onboarding opens for the owner; it fell through to the landing page. */}
      <Route path="/join" element={flagEnabled("onboarding.signup") ? <Register /> : <OperatorRegister />} />
      {/* Same rule: public, outside RequireAuth. Pricing and the policies must
          be readable by someone deciding whether to register at all — putting
          them behind sign-in would mean you could only read the terms after
          agreeing to them. */}
      {/* PUBLIC: an invited sponsor accepts. radaccess answers 503 until the
          provisioner is wired, and this page says "not available". */}
      {flagEnabled("roster") && <Route path="/sponsor/claim" element={<SponsorClaim />} />}
      {flagEnabled("operator.logins") && <Route path="/invite" element={<AcceptInvite />} />}
      <Route path="/pricing" element={<PinnedMode mode="dark"><Pricing /></PinnedMode>} />
      {/* The same document the registration form shows in a dialog, at a URL a
          provider can be sent before they ever open the form. */}
      <Route path="/docs/namespace" element={<NamespaceDoc />} />
      <Route path="/contact" element={<Contact />} />
      {/* PUBLIC: the link in a subscriber's confirm email (§12.3). */}
      <Route path="/confirm-email" element={<ConfirmEmail />} />
      <Route path="/legal" element={<LegalIndex />} />
      <Route path="/legal/:slug" element={<LegalDoc />} />
      {/* PUBLIC: the fixed links in our DLT SMS templates (domain/smsLinks.ts).
          Exact paths, so none can shadow a console screen. */}
      {SMS_LINK_SLUGS.map((slug) => (
        <Route key={slug} path={`/${slug}`} element={<SmsLink slug={slug} />} />
      ))}
      <Route
        path="/"
        element={
          <RequireAuth>
            <Shell />
          </RequireAuth>
        }
      >
        {/* The Welcome Page: the signed-in home. Not "Dashboard" -- the owner's
            rule (2026-09-14) is that the word appears nowhere. */}
        <Route index element={<Welcome />} />
        {/* Provisioning has no backend yet; the page says so. See the screen. */}
        <Route path="provisioning/:resource" element={<ProvisioningScreen />} />
        {/* The person's own authenticator app; a static path, so it outranks
            settings/:item. */}
        <Route path="settings/security" element={<SecurityScreen />} />
        <Route path="settings/:item" element={<SettingsScreen />} />
        {/* The operators beneath this one; entering one changes the namespace
            every screen acts on (auth/scope.ts). */}
        <Route path="providers" element={<ProvidersScreen />} />
        {flagEnabled("operator.logins") && <Route path="staff" element={<StaffScreen />} />}
        {/* Providers › + Add New Provider (owner, 2026-09-19). No flag, NO ROUTE. */}
        {flagEnabled("providers.manage") && <Route path="providers/new" element={<AddProviderScreen />} />}
        {flagEnabled("providers.pins") && <Route path="providers/pins" element={<PinPlansScreen />} />}
        {flagEnabled("providers.pinOrders") && <Route path="providers/pinorders" element={<PinOrdersScreen />} />}
        {flagEnabled("users.invoicePref") && <Route path="users/profiles" element={<SubscriberProfilesScreen />} />}
        {/* Routes come from the catalogue, and only for enabled flags.
            🔑 A disabled screen has NO ROUTE, rather than a route that renders
            a "not enabled" page — so turning a module off in config.json
            removes it from the app rather than hiding it behind a message.
            That is what makes the flag an instant revert instead of a
            cosmetic one. */}
        {/* Same rule as the reports: no flag, NO ROUTE. plans.create builds a
            plan in one call, POST /access/plan -> radcatalog /planspec (plan
            builder design §9.12); it is on only where that route is live. */}
        {flagEnabled("plans.priceList") && <Route path="plans/prices" element={<PriceListScreen />} />}
        {flagEnabled("plans.priceList") && <Route path="plans/documents" element={<PriceListDocumentsScreen />} />}
        {flagEnabled("plans.components") && <Route path="plans/components" element={<ComponentsScreen />} />}
        {flagEnabled("plans.create") && <Route path="plans/create" element={<CreatePlan />} />}
        {flagEnabled("plans.create") && <Route path="plans/on-sale" element={<PlansOnSale />} />}
        {flagEnabled("plans.create") && <Route path="promotions/festive" element={<PlansPromotions />} />}
        {flagEnabled("plans.create") && <Route path="promotions/codes" element={<PlansPromotions section="codes" />} />}
        {flagEnabled("plans.create") && <Route path="promotions/report" element={<PromotionReport />} />}
        {/* The address before the Promotions menu (v0.7.94-v0.7.97), kept for bookmarks. */}
        {flagEnabled("plans.create") && <Route path="plans/promotions" element={<PlansPromotions />} />}
        {/* plans.propose: a rate card in, a price-list PROPOSAL out (design
            §9.13 step 3) -- POST/GET /access/radagent/propose. Writes no price. */}
        {flagEnabled("plans.propose") && <Route path="plans/propose" element={<ProposePriceList />} />}
        {flagEnabled("plans.promptBox") && <Route path="plans/prompt" element={<CreatePlanFromPrompt />} />}
        {flagEnabled("users.create") && <Route path="users/create" element={<CreateUser />} />}
        {flagEnabled("users.import") && <Route path="users/import" element={<ImportCustomers />} />}
        {/* ON by default, unlike the other two: /access/operator IS published
            and radaccess v1.24 IS deployed, so this screen works against a
            live API rather than refusing at a preflight. */}
        {flagEnabled("operator.profile") && (
          <Route path="operator/profile" element={<OperatorProfileScreen />} />
        )}
        {/* OFF unless config.json says true. Needs radhotspotapi v0.1.26 and the
            operator's radhotspot:write grant at the gateway. */}
        {flagEnabled("portal.appearance") && (
          <Route path="portal/appearance" element={<PortalAppearanceScreen />} />
        )}
        {/* OFF unless config.json says true. Needs radaccess v1.52 (the
            operatorcontact write grant and the document routes). */}
        {flagEnabled("legal.documents") && (
          <Route path="operator/legal" element={<LegalDocumentsScreen />} />
        )}
        {/* OFF unless config.json says true. Needs radaccess v1.51 and
            radcustomer v0.2.29 (the roster routes), the org_* tables, and a
            sponsor login linked to its organisation. */}
        {flagEnabled("roster") && <Route path="roster" element={<RosterScreen />} />}
        {/* Operator + superadmin only (radaccess resource rosterorg, v1.65;
            radcustomer v0.2.32). Behind the same roster flag. */}
        {flagEnabled("roster") && <Route path="roster/organisations" element={<OrganisationsScreen />} />}
        {flagEnabled("roster") && <Route path="roster/organisations/:orgId" element={<OrganisationPage />} />}
        {/* Operator + superadmin (radaccess resource subscriberrecharge). */}
        {flagEnabled("billing.recharge") && <Route path="billing/recharge" element={<RechargeScreen />} />}
        {flagEnabled("billing.recharge") && <Route path="users/disable" element={<RechargeScreen mode="disable" />} />}
        {flagEnabled("billing.recharge") && <Route path="billing/print-voucher" element={<RechargeScreen mode="voucher" />} />}
        {/* Owner 2026-10-21: User diagnose moved to Tools; the old address forwards. */}
        {flagEnabled("users.devices") && <Route path="tools/diagnose" element={<RechargeScreen mode="diagnose" />} />}
        {flagEnabled("users.devices") && <Route path="users/diagnose" element={<Navigate to="/tools/diagnose" replace />} />}
        {/* Tools › Test RADIUS login (radaccess v1.134, subscriberdevice:read). */}
        {flagEnabled("tools.radiusTest") && <Route path="tools/radius-test" element={<RechargeScreen mode="radiustest" />} />}
        {/* Payment Gateway and UPI payment notices (owner 2026-10-24, radaccess v1.156). */}
        {flagEnabled("payments.gateway") && <Route path="payments/gateway" element={<PaymentGatewayScreen />} />}
        {flagEnabled("sms.gateway") && <Route path="sms/gateway" element={<SmsGatewayScreen />} />}
        {flagEnabled("sms.gateway") && <Route path="sms/fund" element={<SmsFundScreen />} />}
        {flagEnabled("sms.gateway") && <Route path="sms/funds" element={<SmsFundsPlatformScreen />} />}
        {flagEnabled("billing.upiNotices") && <Route path="billing/upi-notices" element={<UpiNoticesScreen />} />}
        {flagEnabled("connection.requests") && <Route path="billing/connection-requests" element={<ConnectionRequestsScreen />} />}
        {/* Every operator staff role (radaccess resource subscribersearch, v1.120). */}
        {flagEnabled("users.lookup") && <Route path="users/lookup" element={<CustomerLookupScreen />} />}
        {flagEnabled("users.devices") && <Route path="devices/change-onu" element={<RechargeScreen mode="onu" />} />}
        {/* OFF live. Recording (POST /access/payment/orderapi/.../direct) is
            live, but the unpaid-invoice list and customer picker call
            /access/billing/{ns}/unpaidinvoices and /accounts, which radaccess
            does not publish yet -- the screen says so rather than failing. */}
        {flagEnabled("billing.recordPayment") && (
          <Route path="billing/record-payment" element={<RecordPaymentScreen />} />
        )}
        {/* The seller's invoices and receipts per customer: same flag and same
            grant as Record payment, which reads the same tenant ledger. */}
        {flagEnabled("billing.recordPayment") && <Route path="billing/issued" element={<IssuedBillingScreen />} />}
        {flagEnabled("billing.upstream") && <Route path="billing/upstream" element={<UpstreamScreen />} />}
        {flagEnabled("billing.invoiceName") && <Route path="billing/invoice-name" element={<InvoiceNameScreen />} />}
        {flagEnabled("billing.ankhCustomers") && <Route path="billing/customers" element={<AnkhCustomersScreen />} />}
        {flagEnabled("platform.tenants") && <Route path="platform" element={<PlatformScreen />} />}
        {flagEnabled("platform.tenants") && <Route path="platform/plans" element={<PlatformPlansScreen />} />}
        {flagEnabled("billing.ankhInvoices") && (
          <Route path="billing/invoices" element={<AnkhInvoicesScreen />} />
        )}
        {/* Owner 2026-10-25: the Reports categories; the page lists what this login may open. */}
        {REPORT_CATEGORIES.map((c) => (
          <Route key={c.key} path={`reports/${c.key}`} element={<ReportCategoryPage path={`/reports/${c.key}`} />} />
        ))}
        {REPORT_SPECS.filter((r) => flagEnabled(r.flag)).map((r) => (
          <Route
            key={r.slug}
            path={`reports/${r.slug}`}
            element={
              <ReportPage
                title={r.title}
                endpoint={r.endpoint}
                fields={r.fields}
                required={r.required ?? []}
                {...(r.note ? { note: r.note } : {})}
                {...(r.rowsKey ? { rowsKey: r.rowsKey } : {})}
                {...(r.planPrices ? { planPrices: true } : {})}
              />
            }
          />
        ))}
      </Route>
    </Routes>
  );
}
